import { FC } from 'react';
import {
  Button,
  Card,
  Checkbox,
  Dropdown,
  Input,
  RadioGroup,
  RadioGroupItem,
} from '@features/ui';
import {
  PackageContentFormData,
  schemaPackageContent,
} from '@src/types/exchangePackages.ts';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  addonTypeCheckboxData,
  categoryData,
  commissionPercentageCheckboxData,
  demoTypeRadioData,
  packageTypeCheckboxData,
} from '@features/admin/packages/packagesData.ts';

interface PackageContentProps {
  values?: PackageContentFormData;
  onSubmit?: (values: PackageContentFormData) => void;
}
export const PackageContent: FC<PackageContentProps> = ({
  values,
  onSubmit,
}) => {
  const handleSubmitForm = (data: PackageContentFormData) => {
    onSubmit?.(data);
  };

  const { control, handleSubmit } = useForm<PackageContentFormData>({
    resolver: yupResolver(schemaPackageContent),
    defaultValues: values,
  });

  return (
    <form autoComplete="off" onSubmit={handleSubmit(handleSubmitForm)}>
      <div className={'text-xs flex flex-col gap-y-3'}>
        <Controller
          control={control}
          name="name"
          render={({ field }) => (
            <Card className={'flex flex-col gap-y-3.5'}>
              <p>Name(Internal Use)</p>
              <Input variant={'secondary'} {...field} textSize={'xs'} />
            </Card>
          )}
        />
        <Card className={'gap-y-8 items-end'}>
          <div className={'flex lg:flex-row flex-col w-full gap-7.5'}>
            <Controller
              control={control}
              name="packageType"
              render={({ field }) => (
                <div className={'flex flex-col flex-1 gap-y-4.5'}>
                  <p className={'font-bold'}>Package Type</p>
                  <div className={'flex flex-col gap-y-6'}>
                    {packageTypeCheckboxData.map((item) => {
                      return (
                        <div
                          key={item.value}
                          className={
                            'flex gap-x-3.5 items-center min-w-full lg:min-w-[280px]'
                          }
                        >
                          <div className={'flex w-[150px]'}>
                            <Checkbox
                              checked={field.value === item.value}
                              id={`checkbox-${item.value}`}
                              label={item.label}
                              onCheckedChange={() => field.onChange(item.value)}
                            />
                          </div>
                          <Input
                            className={'w-[49px] h-[38px] items-center'}
                            readOnly={true}
                            textAlign={'center'}
                            textSize={'xs'}
                            value={'12'}
                            variant={'secondary'}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            />
            <Controller
              control={control}
              name="revenueSplit"
              render={({ field }) => (
                <div className={'flex flex-col flex-1 gap-y-2 min-w-[280px]'}>
                  <p>Revenue Split</p>
                  <Input
                    className={'w-[52px] h-[38px]'}
                    textSize={'xs'}
                    variant={'secondary'}
                    {...field}
                  />
                </div>
              )}
            />
            <Controller
              control={control}
              name="commissionPercentage"
              render={({ field }) => (
                <div className={'flex flex-1 flex-col gap-y-3.5'}>
                  <p className={'font-bold'}>Commission Percentage (Rule 4)</p>
                  <div className={'flex flex-col gap-y-3.5 min-w-[280px]'}>
                    {commissionPercentageCheckboxData.map((item) => {
                      return (
                        <Checkbox
                          key={item.value}
                          checked={field.value === item.value}
                          id={`checkbox-${item.value}`}
                          label={item.label}
                          onCheckedChange={() => field.onChange(item.value)}
                        />
                      );
                    })}
                  </div>
                </div>
              )}
            />
          </div>
          <div className={'flex lg:flex-row flex-col w-full gap-7.5'}>
            <Controller
              control={control}
              name="category"
              render={({ field }) => (
                <div className={'flex flex-1 flex-col gap-y-5'}>
                  <p className={'font-bold'}>Category</p>
                  <Dropdown
                    className={'rounded-[3px] lg:w-[280px]'}
                    defaultValue={field.value}
                    options={categoryData}
                    placeHolder={'n/a'}
                    size={'sm'}
                    variant={'outline'}
                    onPress={(option) => field.onChange(option.value)}
                  />
                </div>
              )}
            />
          </div>
          <div className={'flex lg:flex-row flex-col w-full gap-7.5'}>
            <Controller
              control={control}
              name="addonTypes"
              render={({ field }) => (
                <div className={'flex flex-1 flex-col gap-y-4.5'}>
                  <p className={'font-bold'}>Addon Types</p>
                  <div className={'flex flex-col gap-y-10'}>
                    {addonTypeCheckboxData.map((item) => {
                      return (
                        <div
                          key={item.value}
                          className={'flex lg:w-[280px] w-full'}
                        >
                          <Checkbox
                            checked={field.value === item.value}
                            id={`checkbox-${item.value}`}
                            label={item.label}
                            onCheckedChange={() => field.onChange(item.value)}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            />
            <Controller
              control={control}
              name="demoType"
              render={({ field }) => (
                <div className={'flex flex-1 flex-col gap-y-3'}>
                  <p>Is this Demo / Bundle Product?</p>
                  <RadioGroup
                    className={'gap-y-10'}
                    defaultValue={field.value}
                    onValueChange={(value) => field.onChange(value)}
                  >
                    {demoTypeRadioData.map((item) => {
                      return (
                        <div
                          key={item.value}
                          className={
                            'flex items-center gap-x-4.5 lg:min-w-[280px] min-w-full'
                          }
                        >
                          <RadioGroupItem value={item.value} />
                          <p>{item.label}</p>
                        </div>
                      );
                    })}
                  </RadioGroup>
                </div>
              )}
            />
            <div className={'flex flex-1'} />
          </div>
          <div className={'flex lg:flex-row flex-col w-full gap-7.5'}>
            <Controller
              control={control}
              name="bundleProduct"
              render={({ field }) => (
                <div
                  className={
                    'flex flex-1 flex-col gap-y-5 lg:min-w-[280px] min-w-full'
                  }
                >
                  <p>Bundle Product</p>
                  <Dropdown
                    className={'rounded-[3px] font-normal'}
                    defaultValue={field.value}
                    options={categoryData}
                    placeHolder={'25 Contract | $ 250,000 Burst | Investor'}
                    size={'sm'}
                    variant={'outline'}
                    onPress={(option) => field.onChange(option.value)}
                  />
                </div>
              )}
            />
            <Controller
              control={control}
              name="bundleProductQty"
              render={({ field }) => (
                <div
                  className={
                    'flex flex-1 flex-col gap-y-5 lg:min-w-[280px] min-w-full'
                  }
                >
                  <p>Bundle Product Qty</p>
                  <Input
                    className={'w-[52px] h-[38px]'}
                    textSize={'xs'}
                    variant={'secondary'}
                    {...field}
                  />
                </div>
              )}
            />
            <div className={'flex flex-1'} />
          </div>
          <Button
            className={'mt-6 mb-1 w-[72px]'}
            size={'sm'}
            type={'submit'}
            variant={'lime'}
          >
            Save
          </Button>
        </Card>
      </div>
    </form>
  );
};
